import React, { useState, useEffect } from 'react';
import Classes from './RecordTable.module.css';
import { Pagination, Stack, IconButton, Modal, Box, Typography } from '@mui/material';
import TableRow from './TableRow';
import { useNavigate } from 'react-router-dom';
import useScreenWidth from '../../../../../../../Hooks/useScreenWidth';
import SkeletonLoading from '../../../../../../GlobalComponents/SkeletonLoading/SkeletonLoading';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';

export default function RecordTable({ report, exposures, setRefresh }) {
  const [loading, setLoading] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const userId = user?._id;
  const navigateTo = useNavigate();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 480;

  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeCountFilter, setActiveCountFilter] = useState('all');
  const [openModal, setOpenModal] = useState(false);
  const [selectedExposure, setSelectedExposure] = useState(null);
  const itemsPerPage = 10;

  const filteredRecords = exposures.filter((record) => {
    const count = Object.values(record.infoExposed).filter(val => val).length;
    const riskMatch = activeFilter === 'all' || record.riskFactor.toLowerCase() === activeFilter;
    let countMatch = true;
    if (activeCountFilter === '0-2') {
      countMatch = count >= 0 && count <= 2;
    } else if (activeCountFilter === '3-5') {
      countMatch = count >= 3 && count <= 5;
    } else if (activeCountFilter === '6+') {
      countMatch = count >= 6;
    }
    return riskMatch && countMatch;
  });

  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const currentItems = filteredRecords.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, filteredRecords.length);
  const totalItems = filteredRecords.length;

  function handlePageChange(e, pageNum) {
    setCurrentPage(pageNum);
  }

  const handleOpenModal = (exposure) => {
    setSelectedExposure(exposure);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedExposure(null);
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: isMobile ? '90%' : 400,
    bgcolor: 'background.paper',
    border: '1px solid #e5e5ef',
    borderRadius: '15px',
    boxShadow: 24,
    p: 4,
    fontFamily: '"Manrope", sans-serif',
  };


  return (
    <div className={Classes.hero}>
      <div className={Classes.header}>
        <div>
          <h3 className={Classes.title3}>Data Record Tracking</h3>
        </div>
        <div className={`${Classes.mlauto} flexAlign5`}>
          <button
            className={`${activeCountFilter === '0-2' ? Classes.activeButton : ''} ${Classes.filterButton}`}
            onClick={() => setActiveCountFilter('0-2')}
          >
            <h5>Low Risk</h5>
          </button>
          <button
            className={`${activeCountFilter === '3-5' ? Classes.activeButton : ''} ${Classes.filterButton}`}
            onClick={() => setActiveCountFilter('3-5')}
          >
            <h5>Medium Risk</h5>
          </button>
          <button
            className={`${activeCountFilter === '6+' ? Classes.activeButton : ''} ${Classes.filterButton}`}
            onClick={() => setActiveCountFilter('6+')}
          >
            <h5>High Risk</h5>
          </button>
          <button
            className={`${activeCountFilter === 'all' ? Classes.activeButton : ''} ${Classes.filterButton}`}
            onClick={() => setActiveCountFilter('all')}
          >
            <h5>All</h5>
          </button>
        </div>
      </div>

      <div className={Classes.table}>
        <div className={Classes.tableWrapper}>
          <div className={Classes.tableHeader}>
            <div className={`${Classes.headerItem} ${Classes.headerCustomer}`}>
              <h5 className="truncate">Broker Name</h5>
            </div>
            <div className={`${Classes.headerItem} ${Classes.headerSpendings}`}>
              <h5>Risk Factor</h5>
            </div>
            <div className={`${Classes.headerItem} ${Classes.headerBookings}`}>
              <h5>Personal Info Exposed</h5>
            </div>
            <div className={`${Classes.headerItem} ${Classes.headerDate}`}>
              <h5>Deletion Status</h5>
            </div>
            <div className={`${Classes.headerItem} ${Classes.headerActions}`}>
            </div>
          </div>

          <div className={Classes.tableBody}>
            {loading ? (
              <SkeletonLoading
                length={itemsPerPage}
                height={isMobile ? '9.5rem' : '4.4rem'}
                width="100%"
                br="6px"
                padding="0"
              />
            ) : currentItems.length > 0 ? (
              currentItems.map((cust, idx) => (
                <TableRow
                  key={cust._id}
                  value={{ ...cust, ID: cust._id }}
                  index={idx}
                  userId={userId}
                  handleOpenModal={handleOpenModal}
                  setRefresh={setRefresh}
                />
              ))
            ) : (
              <h5 className='lightText' style={{ margin: '1rem' }}>No data yet...</h5>
            )}
          </div>

          <div className={Classes.pagFooter}>
            <h5 style={{ height: '1.2rem' }}>
              {loading ? (
                <SkeletonLoading height="1.2rem" width="10rem" br="4px" padding="0" />
              ) : (
                `Showing ${startItem} to ${endItem} of ${totalItems} results`
              )}
            </h5>
            <Stack spacing={2} className={Classes.pagination}>
              {loading
                ? <SkeletonLoading height='1.2rem' width='5rem' br='4px' padding='0' />
                : <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  shape="rounded"
                />
              }
            </Stack>
          </div>
        </div>
      </div>

      <Modal open={openModal} onClose={handleCloseModal}>
        <Box sx={modalStyle}>
          <IconButton
            onClick={handleCloseModal}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
          {selectedExposure && (
            <>
              <Typography variant="h6" sx={{ mb: 2, fontFamily: '"Manrope", sans-serif', fontWeight: 600 }}>
                Exposure Details
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, fontFamily: '"Manrope", sans-serif' }}>
                <strong>Broker:</strong> {selectedExposure.brokerName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, fontFamily: '"Manrope", sans-serif' }}>
                <strong>Risk Factor:</strong> {selectedExposure.riskFactor}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, fontFamily: '"Manrope", sans-serif' }}>
                <strong>Deletion Status:</strong> {selectedExposure.deletionStatus}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: '"Manrope", sans-serif' }}>
                <strong>Exposed Info:</strong>
                <ul className={Classes.exposedList}>
                  {Object.entries(selectedExposure.infoExposed || {})
                    .filter(([_, exposed]) => exposed)
                    .map(([key]) => (
                      <li key={key} className={Classes.exposedItem}>
                        {getIconForInfo(key)} {key.charAt(0).toUpperCase() + key.slice(1)}
                      </li>
                    ))}
                </ul>
              </Typography>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
}

function getIconForInfo(key) {
  switch (key) {
    case 'name':
      return <span className={Classes.exposedIcon}>👤</span>;
    case 'email':
      return <span className={Classes.exposedIcon}>✉️</span>;
    case 'phone':
      return <span className={Classes.exposedIcon}>📞</span>;
    case 'address':
      return <span className={Classes.exposedIcon}>🏠</span>;
    case 'photo':
      return <span className={Classes.exposedIcon}>📷</span>;
    case 'socialMedia':
      return <span className={Classes.exposedIcon}>🌐</span>;
    case 'occupation':
      return <span className={Classes.exposedIcon}>💼</span>;
    case 'courtRecords':
      return <span className={Classes.exposedIcon}>⚖️</span>;
    case 'maritalStatus':
      return <span className={Classes.exposedIcon}>💍</span>;
    case 'propertyValue':
      return <span className={Classes.exposedIcon}>🏡</span>;
    case 'age':
      return <span className={Classes.exposedIcon}>🎂</span>;
    case 'postAddress':
      return <span className={Classes.exposedIcon}>📍</span>;
    default:
      return <span className={Classes.exposedIcon}>ℹ️</span>;
  }
}